<template>
    <main class="content">
      <!-- Always visible language selector at top right -->
      <div v-if="!featureFlags.hideLanguageSelector" class="fixed-language-selector">
        <LanguageSelector @language-changed="onLanguageChanged" />
      </div>
      
      <top-nav :floating="true" />
      <header class="intro" :class="{ hidden: hideHeader }">
        <h1 class="title">{{ $t('home.title') }}</h1>
        <p class="subtitle">
          {{ $t('home.subtitle') }}
        </p>
      </header>
  
      <section class="image-container">
        <picture>
          <source srcset="/MijiDrawing.webp" type="image/webp" />
          <img loading="lazy"  class="portfolio-image" src="/MijiDrawing.png" :alt="$t('alt.mijiDrawing')" />
        </picture>
      </section>

      <div class="banner-container">
        <svg
          class="banner-shape"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <path d="M0,0 L0,100 L100,100 L100,0 L50,90 Z" fill="#f7f0dc" />
          
        </svg>
      </div>

      <div class="portfolio-break" style="height: 400px;"></div>
  
      <section class="portfolio-section">
        <h2 class="section-heading">{{ $t('home.sectionHeading') }}</h2>
        <div class="portfolio-grid">
          <div 
            class="portfolio-item" 
            v-for="item in portfolioItems" 
            :key="item.id"
            @click="navigateToSection(item)"
            :class="{ 'clickable': isNavigableItem(item) }"
          >
            <picture>
              <source :srcset="item.image.replace('.png', '.webp')" type="image/webp" />
              <img loading="lazy" :src="item.image" :alt="$t(`home.portfolioItems.${item.key}.title`)" />
            </picture>
            <h3>{{ $t(`home.portfolioItems.${item.key}.title`) }}</h3>
            <p>{{ $t(`home.portfolioItems.${item.key}.description`) }}</p>

            <!-- Navigation hint for clickable items -->
            <div v-if="isNavigableItem(item)" class="navigation-hint">
              <span class="hint-text">{{ $t('home.navigationHint') }}</span>
            </div>

            <!-- External link for Content Creator -->
            <div v-else-if="item.key === 'contentCreator'">
              <a
                class="drawer-button"
                href="https://www.youtube.com/@mizipmizip"
                target="_blank"
                rel="noopener noreferrer"
                @click.stop
              >
                {{ $t('home.portfolioItems.contentCreator.youtubeButton') }}
              </a>
            </div>

          </div>

        </div>
      </section>

      <div class="portfolio-break" style="height: 200px;"></div>

      <section id="graphic-design" class="carousel-section">
        <h2 class="section-heading">{{ $t('home.thingsIveMade') }}</h2>
        <h3 class="subsection-heading">{{ $t('home.subsections.graphicImageMedia') }}</h3>
        <div class="carousel-container" ref="carouselContainer">
          <img
            v-for="(img, index) in carouselImages"
            :key="index"
            :src="img.image"
            :alt="img.alt"
            class="carousel-image"
            loading="lazy"
            @click="openPopup(img.image, img.alt)"
          />
        </div>
      </section>

      <div v-if="!featureFlags.hideVideoSection" class="portfolio-break" style="height: 100px;"></div>

      <section v-if="!featureFlags.hideVideoSection" id="videos" class="videos-section">
        <h3 class="subsection-heading">{{ $t('home.subsections.videosAdvertisements') }}</h3>
        <div class="videos-container">
          <div
            v-for="(video, index) in videoItems"
            :key="index"
            class="video-item"
            @click="openVideoPopup(video)"
          >
            <img
              :src="video.thumbnail"
              :alt="video.alt"
              class="video-thumbnail"
              loading="lazy"
            />
            <div class="video-overlay">
              <div class="play-button">▶</div>
            </div>
            <div class="video-info" v-if="video.title">
              <h4>{{ video.title }}</h4>
              <p v-if="video.description">{{ video.description }}</p>
            </div>
          </div>
        </div>
      </section>

      <div v-if="!featureFlags.hideCoffeeSection" class="portfolio-break" style="height: 100px;"></div>

      <section v-if="!featureFlags.hideCoffeeSection" id="coffee" class="coffee-section">
        <h3 class="subsection-heading">{{ $t('home.subsections.coffeeAndRoasting') }}</h3>
        <div class="coffee-container">
          <div
            v-for="(coffee, index) in coffeeItems"
            :key="index"
            class="coffee-item"
            @click="openPopup(coffee.image, coffee.alt)"
          >
            <img
              :src="coffee.image"
              :alt="coffee.alt"
              class="coffee-image"
              loading="lazy"
            />
            <div class="coffee-info" v-if="coffee.title">
              <h4>{{ coffee.title }}</h4>
              <p v-if="coffee.description">{{ coffee.description }}</p>
            </div>
          </div>
        </div>
      </section>

      <div class="image-popup" v-if="popupImage">
        <div class="popup-overlay" @click="closePopup"></div>
        <div class="popup-content">
          <button class="popup-close" @click="closePopup">✕</button>
          <img :src="popupImage" :alt="popupAlt" />
        </div>
      </div>

      <div class="video-popup" v-if="popupVideo">
        <div class="popup-overlay" @click="closeVideoPopup"></div>
        <div class="popup-content">
          <button class="popup-close" @click="closeVideoPopup">✕</button>
          <video :src="popupVideo.video" controls autoplay>
            {{ $t('video.unsupportedMessage') }}
          </video>
          <div class="video-popup-info" v-if="popupVideo.title">
            <h4>{{ popupVideo.title }}</h4>
            <p v-if="popupVideo.description">{{ popupVideo.description }}</p>
          </div>
        </div>
      </div>

      <section id="about" class="about-section">
        <video 
          class="about-video" 
          src="/MijiWalking.webm" 
          autoplay 
          loop 
          muted 
          playsinline 
          :aria-label="$t('alt.mijiWalkingAnimation')"
          width="180" 
          height="180">
          <source src="/MijiWalking.webm" type="video/webm">
          <img class="about-image" src="/MijiWalking.gif" :alt="$t('alt.mijiWalkingAnimation')" width="180" height="180" />
        </video>


        <h2 class="section-heading">{{ $t('home.about.heading') }}</h2>

        <div class="about-content">
          <p>
            {{ $t('home.about.description') }}
          </p>
          <p class="email-callout" v-html="$t('home.about.emailCallout')">
          </p>
        </div>
      </section>


    </main>
  </template>
  
  <script>
  //import matter from 'gray-matter';
  import TopNav from '@/components/TopNav.vue';
  import LanguageSelector from '@/components/LanguageSelector.vue';
  import translationMixin from '@/mixins/translationMixin.js';
  import featureFlags from '@/config/featureFlags.js';
  
  export default {
    name: 'HomePage',
    mixins: [translationMixin],
    components: {
      TopNav,
      LanguageSelector,
    },
    data() {
      return {
        featureFlags,
        showExamples: false,
        activeDrawerId: null, 
        hideHeader: false,
        bubblePool: [
          'Designing Newsletters',
          'Scheduling Meetings',
          'Creating Instagram Posts',
          'Planning Events',
          'Writing Blog Articles',
          'Coordinating Photoshoots',
          'Editing Videos',
          'Building Email Campaigns',
          'Drafting Brand Guidelines',
          'Managing Spreadsheets',
          'Responding to Client Emails',
          'Organizing Dropbox Files',
        ],
        visibleBubbles: [],
        bubbleCount: 0,
        carouselImages: [],
        videoItems: [],
        coffeeItems: [],
        popupImage: null,
        popupAlt: '',
        popupVideo: null,
        autoScrollInterval: null,
        portfolioItems: [
          {
            id: 1,
            key: 'administrativeAssistant',
            image: '/MijiPhone.png',
            category: 'Administration'
          },
          {
            id: 2,
            key: 'contentCreator',
            image: '/MijiFilming.png',
            category: 'Content Creation'
          },
          {
            id: 3,
            key: 'graphicDesigner',
            image: '/MijiDesign.png',
            category: 'Graphic Design'
          },
          {
            id: 4,
            key: 'coffeeCreator',
            image: '/MijiCoffee.png',
            category: 'Coffee Creation'
            },
          {
            id: 5,
            key: 'socialMediaManager',
            image: '/MijiSocial.png',
            category: 'Social Media Management'
          },
          {
            id: 6,
            key: 'koreanCommunication',
            image: '/MijiKorean.png',
            category: 'Korean Communication'
          },
        ]
      };
    },
    mounted() {
      window.addEventListener('scroll', this.handleScroll);
      this.loadCarousel();
      
      // Only load videos if the section is not hidden
      if (!this.featureFlags.hideVideoSection) {
        this.loadVideos();
      }
      
      // Only load coffee if the section is not hidden
      if (!this.featureFlags.hideCoffeeSection) {
        this.loadCoffee();
      }
    },
    beforeDestroy() {
      window.removeEventListener('scroll', this.handleScroll);
    },
    methods: {
      handleScroll() {
        const bannerHeight = 300; // match your .banner-container height
        this.hideHeader = window.scrollY > bannerHeight - 60; // fade out just before it reaches the banner
      },
      toggleDrawer(id) {
        this.activeDrawerId = this.activeDrawerId === id ? null : id;
      },
      isNavigableItem(item) {
        const navigableItems = ['graphicDesigner'];
        
        // Only add contentCreator if video section is not hidden
        if (!this.featureFlags.hideVideoSection) {
          navigableItems.push('contentCreator');
        }
        
        // Only add coffeeCreator if coffee section is not hidden
        if (!this.featureFlags.hideCoffeeSection) {
          navigableItems.push('coffeeCreator');
        }
        
        return navigableItems.includes(item.key);
      },
      navigateToSection(item) {
        if (!this.isNavigableItem(item)) return;
        
        let targetId = '';
        switch (item.key) {
          case 'graphicDesigner':
            targetId = 'graphic-design';
            break;
          case 'contentCreator':
            if (!this.featureFlags.hideVideoSection) {
              targetId = 'videos';
            }
            break;
          case 'coffeeCreator':
            if (!this.featureFlags.hideCoffeeSection) {
              targetId = 'coffee';
            }
            break;
        }
        
        if (targetId) {
          const targetSection = document.getElementById(targetId);
          if (targetSection) {
            targetSection.scrollIntoView({ 
              behavior: 'smooth',
              block: 'center'
            });
          }
        }
      },
      async loadCarousel() {
        const res = await fetch('/portfolio-carousel.json');
        this.carouselImages = await res.json();
      },
      async loadVideos() {
        try {
          const res = await fetch('/portfolio-videos.json');
          this.videoItems = await res.json();
        } catch (error) {
          console.log('No videos data found');
          this.videoItems = [];
        }
      },
      async loadCoffee() {
        try {
          const res = await fetch('/portfolio-coffee.json');
          this.coffeeItems = await res.json();
        } catch (error) {
          console.log('No coffee data found');
          this.coffeeItems = [];
        }
      },
      startAutoScroll() {
        const container = this.$refs.carouselContainer;
        this.autoScrollInterval = setInterval(() => {
          if (!container) return;
          container.scrollLeft += 1;
          if (container.scrollLeft >= container.scrollWidth - container.clientWidth) {
            container.scrollLeft = 0;
          }
        }, 20);
      },
      openPopup(image, alt) {
        clearInterval(this.autoScrollInterval);
        this.popupImage = image;
        this.popupAlt = alt;
      },
      closePopup() {
        this.popupImage = null;
        this.popupAlt = '';
        this.startAutoScroll();
      },
      openVideoPopup(video) {
        clearInterval(this.autoScrollInterval);
        this.popupVideo = video;
      },
      closeVideoPopup() {
        this.popupVideo = null;
        this.startAutoScroll();
      },
      onLanguageChanged(language) {
        // Handle language change event if needed
        console.log('Language changed to:', language);
      }
    }
  };
  </script>
  
  <style>
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600&family=Source+Serif+Pro:wght@400;600&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Merriweather:wght@400;700&display=swap');  /* New Yorker-style font */

html, body {
  width: 100%;
  margin: 0;
  overflow-x: hidden; /* Only hide horizontal overflow globally */
}

/* Fixed language selector always visible at top right */
.fixed-language-selector {
  position: fixed;
  top: 1rem;
  right: 2rem;
  z-index: 2000; /* Higher than TopNav's z-index of 1000 */
  background-color: rgba(250, 250, 250, 0.95);
  padding: 0.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
}

/* Mobile responsive styles for fixed language selector */
@media (max-width: 768px) {
  .fixed-language-selector {
    top: 0.75rem;
    right: 1rem;
    padding: 0.4rem;
  }
}

@media (max-width: 480px) {
  .fixed-language-selector {
    top: 0.5rem;
    right: 0.75rem;
    padding: 0.3rem;
  }
}

.content {
    font-family: 'Source Serif Pro', Georgia, serif;
    color: #111;
    background-color: #fdfdf3;
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0; /* <-- Remove padding */
}

/* Carousel Items */
.carousel-image {
  width: 150px;
  height: 350px;
  object-fit: cover;
  object-position: center;
  border-radius: 6px;
  margin: 0 10px;
}

.carousel-container {
  display: flex;
  justify-content: center;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 2rem 1rem;
}

/* Section Headings */
.subsection-heading {
  font-family: 'Playfair Display', serif;
  font-size: 1.8rem;
  font-weight: 600;
  color: #333;
  text-align: center;
  margin: 2rem 0 1.5rem 0;
}

/* Videos Section */
.videos-container {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  padding: 2rem 1rem;
}

.video-item {
  position: relative;
  width: 200px;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.video-item:hover {
  transform: scale(1.05);
}

.video-thumbnail {
  width: 100%;
  height: 150px;
  object-fit: cover;
  border-radius: 8px;
}

.video-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 150px;
  background: rgba(0, 0, 0, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.video-item:hover .video-overlay {
  opacity: 1;
}

.play-button {
  color: white;
  font-size: 2rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.video-info {
  padding: 0.5rem 0;
  text-align: center;
}

.video-info h4 {
  margin: 0.5rem 0 0.25rem 0;
  font-size: 1rem;
  color: #333;
}

.video-info p {
  margin: 0;
  font-size: 0.9rem;
  color: #666;
  line-height: 1.3;
}

/* Coffee Section */
.coffee-container {
  display: flex;
  justify-content: center;
  gap: 1.5rem;
  flex-wrap: wrap;
  padding: 2rem 1rem;
}

.coffee-item {
  width: 200px;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.coffee-item:hover {
  transform: scale(1.05);
}

.coffee-image {
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-radius: 8px;
}

.coffee-info {
  padding: 0.5rem 0;
  text-align: center;
}

.coffee-info h4 {
  margin: 0.5rem 0 0.25rem 0;
  font-size: 1rem;
  color: #333;
}

.coffee-info p {
  margin: 0;
  font-size: 0.9rem;
  color: #666;
  line-height: 1.3;
}

.image-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.popup-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: white;
  border-radius: 8px;
  padding: 1rem;
}

.popup-content img {
  max-width: 100%;
  max-height: 80vh;
  display: block;
  margin: 0 auto;
  object-fit: contain;
}

.popup-close {
  position: absolute;
  top: 0.25rem;
  right: 0.5rem;
  font-size: 1.5rem;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #333;
}

/* Video Popup */
.video-popup {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.8);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-popup .popup-content {
  max-width: 90vw;
  max-height: 90vh;
  background: #000;
  border-radius: 8px;
  padding: 1rem;
}

.video-popup video {
  max-width: 100%;
  max-height: 70vh;
  display: block;
  margin: 0 auto;
}

.video-popup-info {
  color: white;
  text-align: center;
  margin-top: 1rem;
}

.video-popup-info h4 {
  margin: 0.5rem 0;
  font-size: 1.2rem;
}

.video-popup-info p {
  margin: 0;
  font-size: 1rem;
  opacity: 0.8;
}

.about-section {
  width: 100vw;
  padding: 3rem 1rem;
  background-color: #fdfdf3;
  text-align: center;
}

.about-video {
  width: 300px;
  height: 300px;
  display: block;
  margin: 2rem auto;
  background: transparent;
}


.about-image {
  width: 90%;
  max-width: 500px;
  height: auto;
  display: block;
  margin: 2rem auto;
}


.about-content {
  max-width: 800px;
  margin: 0 auto;
  padding-left: 15px;
  padding-right: 15px;
  font-size: 1.25rem;
  line-height: 1.8;
  color: #444;
}

.about-content .email-callout {
  margin-top: 2rem;
  font-size: 1.2rem;
}

.about-content .email-callout a {
  color: #d26a00;
  text-decoration: underline;
  font-weight: bold;
}

.about-content .email-callout a:hover {
  color: #b25400;
}

.intro {
  position: fixed;  /* Keeps it fixed at the top */
  top: 0;
  left: 0;
  width: 100%; /* Ensure it takes up the full width */
  z-index: 0; /* Ensure it stays above the rest of the content */
  text-align: center;
  margin-bottom: 2rem;
  background-color: #fdfdf3; /* Optional: ensures the background is consistent */
  transition: opacity 0.01s ease; /* Add a smooth fade-out effect */
}

.intro.hidden {
  opacity: 0; /* Make the intro fade out as it scrolls */
  pointer-events: none; /* Prevent interaction when hidden */
}
  
.title {
  font-family: 'Playfair Display', serif;
  font-size: 4rem;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
}

.subtitle {
  font-size: 1.25rem;
  color: #555;
  font-style: italic;
}
  
.image-container {
  position: absolute;
  top: 180px; /* Moves image into the notch - adjust as needed */
  left: 50%;
  transform: translateX(-50%);
  z-index: 2;
  width: 100%;
  display: flex;
  justify-content: center;
  pointer-events: none; /* optional: allows scrolls through image area */

}

.banner-container {
  width: 100vw; /* Full screen width */
  height: 400px;
  position: relative;
  z-index: 1;
  margin-top: 0;
  margin-left: 0;
  margin-right: 0;
}


.banner-shape {
  width: 100%;
  height: 100%;
  display: block;
  position: absolute;
  top: 0;
  left: 0;
}

.drawer-button {
  margin-top: 1rem;
  background-color: #d26a00;
  color: white;
  padding: 0.5rem 1rem;
  border: none;
  font-weight: bold;
  cursor: pointer;
  border-radius: 5px;
  transition: background-color 0.2s ease;
}

.drawer-button:hover {
  background-color: #b25400;
}

.drawer {
  margin-top: 1rem;
  padding: 1rem;
  background-color: #fff7ea;
  border: 1px solid #e5c9a6;
  border-radius: 8px;
}

.carousel-placeholder {
  height: 150px;
  background-color: #ddd;
  display: flex;
  justify-content: center;
  align-items: center;
  color: #333;
  font-weight: bold;
}

  

  
  /* Portfolio Section */
.portfolio-section {
  width: 100vw; /* Full screen width */
  margin-top: 0;
  background-color: #f7f0dc;
  padding: 2rem 0; /* Vertical padding only */
}
.portfolio-image {
  width: 90%;
  max-width: 500px;
  height: auto;
}
.portfolio-break {
  width: 100vw; /* Full screen width */
  margin-top: -1px;
  background-color: #f7f0dc;
  z-index: 1;
}

  
.section-heading {
  font-family: 'Playfair Display', serif;
  font-size: 3rem;
  margin-bottom: 1.5rem;
  text-align: center; /* Optional: or keep left-aligned */
  padding-bottom: 0.5rem;
  max-width: 1350px;
  margin-left: auto;
  margin-right: auto;
}
  
.portfolio-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 2rem;
  max-width: 1350px;
  margin: 0 auto;
}
  
.portfolio-item {
  padding: 1rem;
  text-align: left;
  max-width: 450px;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.portfolio-item.clickable {
  cursor: pointer;
}

.portfolio-item.clickable:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.portfolio-item:nth-child(1),
.portfolio-item:nth-child(2),
.portfolio-item:nth-child(4),
.portfolio-item:nth-child(5) {
  position: relative; /* required for absolutely positioned pseudo-element */
}

.portfolio-item:nth-child(1)::after,
.portfolio-item:nth-child(2)::after,
.portfolio-item:nth-child(4)::after,
.portfolio-item:nth-child(5)::after {
  content: '';
  position: absolute;
  top: 25%; /* start 25% down */
  right: 0;
  width: 1px;
  height: 50%; /* cover 50% of the item height */
  background-color: #ccc;
}
  
  .portfolio-item img {
    width: 100%;
    height: auto;
    margin-bottom: 1rem;
  }
  
  .portfolio-item h3 {
    font-family: 'Merriweather', serif; /* Apply Merriweather for a 'New Yorker' style */
    font-size: 1.5rem;
    margin: 0.5rem 0;
  }
  
  .portfolio-item p {
    font-size: 1.25rem;
    color: #444;
  }

  /* Navigation hint styles */
  .navigation-hint {
    margin-top: 1rem;
    padding: 0.5rem;
    background-color: rgba(210, 106, 0, 0.1);
    border-left: 3px solid #d26a00;
    border-radius: 4px;
  }

  .hint-text {
    color: #d26a00;
    font-weight: 600;
    font-size: 0.9rem;
  }

  @media (max-width: 768px) {
  /* Shrink drawing image */
  .portfolio-image {
    margin-top: 20px;
    margin-left: 15%;
    width: 80%; /* About 50% smaller */
  }

  .about-image {
    margin-top: 125px;
    margin-left: 25%;
    margin-right: auto;
    width: 60%;
    display: block;
  }

  .title {
  font-family: 'Playfair Display', serif;
  font-size: 3rem;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
  padding-top: 20px;
}

.subtitle {
  font-size: .9rem;
  color: #555;
  font-style: italic;
}

  /* Shrink banner height */
  .banner-container {
    height: 200px;
    margin-top: 20px; /* Add top gap */
  }

  /* Prevent the banner from scaling */
  .banner-shape {
    width: 250%; /* Keep the width same as before */
    left: -75%; /* Center the banner */
    padding-top: 20px;
    position: relative;
  }
  
  /* Stack portfolio items in single column */
  .portfolio-section{
    padding-top: 150px;
  }

  .section-heading{
    font-size: 2rem;
  }

  .portfolio-grid {
    grid-template-columns: 1fr;
    padding: 0 1rem;
  }

  /* Optional: center-align text inside items */
  .portfolio-item {
    text-align: center;
    max-width: 100%;
  }

  .portfolio-item img {
    margin: 0 auto 1rem;
  }

  .portfolio-item:nth-child(1),
  .portfolio-item:nth-child(2),
  .portfolio-item:nth-child(4),
  .portfolio-item:nth-child(5){
    border-right: 0px solid #ccc;
}
}

  </style>